"use client"

import { useState } from "react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { Menu, X, Phone, MessageCircle } from "lucide-react"
import Image from "next/image"

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const phoneNumber = "5551983204177"

  return (
    <header className="fixed top-0 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-50 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link href="/" className="flex items-center" aria-label="Void Agência - Página inicial">
            <Image
              src="/images/void-logo.svg"
              alt="Void Agência - Logo"
              width={120}
              height={30}
              className="h-8 w-auto"
              priority
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8" role="navigation" aria-label="Menu principal">
            <Link
              href="#services"
              className="text-slate-300 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-slate-900 rounded px-2 py-1"
              aria-label="Ver nossos serviços"
            >
              Serviços
            </Link>
            <Link
              href="#pricing"
              className="text-slate-300 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-slate-900 rounded px-2 py-1"
              aria-label="Ver nossos planos"
            >
              Planos
            </Link>
            <Link
              href="#team"
              className="text-slate-300 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-slate-900 rounded px-2 py-1"
              aria-label="Conhecer nossa equipe"
            >
              Equipe
            </Link>
            <Link
              href="#contact"
              className="text-slate-300 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-slate-900 rounded px-2 py-1"
              aria-label="Entrar em contato"
            >
              Contato
            </Link>
          </nav>

          {/* Desktop Action Buttons */}
          <div className="hidden md:flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <a
                href={`tel:+${phoneNumber}`}
                className="flex items-center gap-2"
                aria-label="Ligar para (51) 98320-4177"
              >
                <Phone size={16} aria-hidden="true" />
                Ligar
              </a>
            </Button>
            <Button size="sm" asChild>
              <a
                href={`https://wa.me/${phoneNumber}?text=Olá! Gostaria de saber mais sobre os serviços da Void Agência.`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2"
                aria-label="Conversar no WhatsApp"
              >
                <MessageCircle size={16} aria-hidden="true" />
                WhatsApp
              </a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-white hover:text-purple-400 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-slate-900 rounded"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Fechar menu" : "Abrir menu"}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMenuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav
            className="md:hidden py-4 border-t border-border bg-background"
            id="mobile-menu"
            role="navigation"
            aria-label="Menu mobile"
          >
            <div className="flex flex-col space-y-4">
              <Link
                href="#services"
                className="text-slate-300 hover:text-white transition-colors py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-slate-900 rounded px-2"
                onClick={() => setIsMenuOpen(false)}
                aria-label="Ver nossos serviços"
              >
                Serviços
              </Link>
              <Link
                href="#pricing"
                className="text-slate-300 hover:text-white transition-colors py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-slate-900 rounded px-2"
                onClick={() => setIsMenuOpen(false)}
                aria-label="Ver nossos preços e planos"
              >
                Preço
              </Link>
              <Link
                href="#team"
                className="text-slate-300 hover:text-white transition-colors py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-slate-900 rounded px-2"
                onClick={() => setIsMenuOpen(false)}
                aria-label="Conhecer nossa equipe"
              >
                Equipe
              </Link>
              <Link
                href="#contact"
                className="text-slate-300 hover:text-white transition-colors py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-slate-900 rounded px-2"
                onClick={() => setIsMenuOpen(false)}
                aria-label="Entrar em contato"
              >
                Contato
              </Link>
              <div className="flex flex-col gap-2 pt-2">
                <Button variant="outline" size="sm" asChild>
                  <a
                    href={`tel:+${phoneNumber}`}
                    className="flex items-center gap-2 justify-center"
                    aria-label="Ligar para (51) 98320-4177"
                  >
                    <Phone size={16} aria-hidden="true" />
                    (51) 98320-4177
                  </a>
                </Button>
                <Button size="sm" asChild>
                  <a
                    href={`https://wa.me/${phoneNumber}?text=Olá! Gostaria de saber mais sobre os serviços da Void Agência.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 justify-center"
                    onClick={() => setIsMenuOpen(false)}
                    aria-label="Conversar no WhatsApp"
                  >
                    <MessageCircle size={16} aria-hidden="true" />
                    WhatsApp
                  </a>
                </Button>
              </div>
            </div>
          </nav>
        )}
      </div>
    </header>
  )
}
